import React, { useEffect, useMemo, useRef, useState } from "react";
import { BiSortDown, BiSortUp } from "react-icons/bi";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";

function Sort({ sortBy = {}, updateSort = () => {} }) {
	const [showMenu, setShowMenu] = useState(false);
	const containerRef = useRef(null);
	const sortTypes = [
		{
			type: "departure",
			label: "Departure Time",
		},
		{
			type: "duration",
			label: "Duration",
		},
		{
			type: "arrival",
			label: "Arrival Time",
		},
		{
			type: "price",
			label: "Price",
		},
	];

	const currentSort = useMemo(() => sortTypes.find((s) => s.type === sortBy.type) || sortTypes[0], [sortBy.type]);
	const currentOrder = sortBy.order === "des" ? "des" : "asc";

	const getOrderLabel = (type, order) => {
		if (type === "price") return order === "asc" ? "Low to High" : "High to Low";
		if (type === "duration") return order === "asc" ? "Shortest first" : "Longest first";
		if (type === "departure") return order === "asc" ? "Earliest first" : "Latest first";
		if (type === "arrival") return order === "asc" ? "Earliest first" : "Latest first";
		return order === "asc" ? "Ascending" : "Descending";
	};

	const setSort = (type, order) => {
		updateSort({ type, order });
		setShowMenu(false);
	};

	useEffect(() => {
		if (!showMenu) {
			return undefined;
		}

		const handlePointerDown = (event) => {
			if (!containerRef.current?.contains(event.target)) {
				setShowMenu(false);
			}
		};

		const handleEscape = (event) => {
			if (event.key === "Escape") {
				setShowMenu(false);
			}
		};

		document.addEventListener("mousedown", handlePointerDown);
		document.addEventListener("touchstart", handlePointerDown);
		document.addEventListener("keydown", handleEscape);

		return () => {
			document.removeEventListener("mousedown", handlePointerDown);
			document.removeEventListener("touchstart", handlePointerDown);
			document.removeEventListener("keydown", handleEscape);
		};
	}, [showMenu]);

	return (
		<>
			<div ref={containerRef} className="relative">
				<button type="button" onClick={() => setShowMenu((prev) => !prev)} className="h-9 px-3 text-xs border rounded-md align-center bg-canvas border-surface">
					<span className="mr-1 text-secondary">Sort:</span>
					<span className="font-semibold text-dark">{currentSort?.label}</span>
					<span className="ml-1 text-secondary">({getOrderLabel(currentSort?.type, currentOrder)})</span>
					{showMenu ? <FiChevronUp className="ml-2" /> : <FiChevronDown className="ml-2" />}
				</button>
				{showMenu && (
					<div className="absolute left-0 right-0 sm:right-auto sm:w-[22rem] z-20 p-1 mt-2 border rounded-md shadow-md bg-canvas border-surface">
						{sortTypes.map(({ type, label }, i) => {
							const activeAsc = sortBy.type === type && currentOrder === "asc";
							const activeDes = sortBy.type === type && currentOrder === "des";
							return (
								<div key={i} className={`${i ? "mt-1" : ""} rounded border border-surface/60`}>
									<div className="px-2 py-1.5 text-[11px] font-semibold text-secondary bg-light/70">{label}</div>
									<button
										type="button"
										onClick={() => setSort(type, "asc")}
										className={`h-8 px-2 w-full text-left text-xs flex items-center justify-between ${activeAsc ? "bg-light text-dark font-semibold" : "text-secondary hover:bg-light"}`}
									>
										<span>Asc - {getOrderLabel(type, "asc")}</span>
										{activeAsc ? <BiSortUp /> : null}
									</button>
									<button
										type="button"
										onClick={() => setSort(type, "des")}
										className={`h-8 px-2 w-full text-left text-xs border-t border-surface/60 flex items-center justify-between ${activeDes ? "bg-light text-dark font-semibold" : "text-secondary hover:bg-light"}`}
									>
										<span>Desc - {getOrderLabel(type, "des")}</span>
										{activeDes ? <BiSortDown /> : null}
									</button>
								</div>
							);
						})}
					</div>
				)}
			</div>
		</>
	);
}

export default Sort;
